/* eslint-disable react/no-array-index-key */
import { Col, Row } from 'antd';
import Avatar from 'antd/lib/avatar/avatar';
import AddressFormatComponent from 'components/common/AddressFormatComponent';
import LinkedDocument from 'components/common/LinkedDocument';
import InputFieldsGenerator from 'components/InputFieldsGenerator';
import { DataFieldProps } from 'components/InputFieldsGenerator/types';
import MapDirection from 'components/MapDirection';
import { FORMAT_DATE_TIME_FOR_DISPLAY } from 'constants/days';
import useCurrentLoggedInUser from 'hooks/apollo/queries/useCurrentLoggedInUser';
import useRoleCheck from 'hooks/useRoleCheck';
import moment from 'moment';
import React, { useMemo } from 'react';
import { TextOnlyButtonStyled, TextOnlyStyled } from 'routes/Transaction/components/styled';
import { AppointmentStatus, IAppointment } from 'types/appointments';
import { GeneralFunction } from 'types/common';
import { ITransactionType } from 'types/services';
import { RoleEnum } from 'types/user';
import { getDefaultAvatarUrl } from 'utils';

import { useCommonActionButtonsForAppointments } from '../NotaryAppointments/helpers';
import { getPaymentText } from './helpers';
import { NotaryNameStyled } from './styled';

const AppointmentSlideIn: React.FC<{
  selectedAppointment?: IAppointment;
  showNotaryModal?: GeneralFunction;
  transactionTypesData?: ITransactionType[];
  refetchAppointments?: GeneralFunction;
}> = ({ selectedAppointment, transactionTypesData, refetchAppointments }) => {
  const { isAdmin, isNotary } = useRoleCheck();
  const { Modal, assignNotaryClick } = useCommonActionButtonsForAppointments({
    appointment: (selectedAppointment || {}) as IAppointment,
    refetch: refetchAppointments,
    transactionTypesData,
  });

  const signatoryIds = selectedAppointment?.signatoryIds || [];
  const description = selectedAppointment?.description;
  const duration = selectedAppointment?.duration || 0;
  const address = selectedAppointment?.address || {};
  const date = moment(selectedAppointment?.date || '').format(FORMAT_DATE_TIME_FOR_DISPLAY);
  const dateTime = date?.toString() || '';

  const signatoriesData = selectedAppointment?.transaction?.signatories?.reduce((acc, signatory) => {
    acc[signatory.id] = signatory;
    return acc;
  }, {} as any);
  const selectedSignatories =
    signatoriesData && signatoryIds
      ? signatoryIds.map((sigId: string) => `${signatoriesData[sigId]?.firstName} ${signatoriesData[sigId]?.lastName}`)
      : 'None';

  const notary = selectedAppointment?.notary;
  const isAppointmentRequested = selectedAppointment?.status === AppointmentStatus.REQUESTED;
  const userRole = useCurrentLoggedInUser().role;

  const NotaryDataField = useMemo(() => {
    const notaryFullName = `${notary?.firstName} ${notary?.lastName}`;
    switch (userRole) {
      case RoleEnum.ADMIN:
        return (
          <div className='items-center flex-nowrap flex'>
            {!!notary && (
              <>
                <div className='flex items-center' style={{ maxWidth: 160 }}>
                  <Col flex='32'>
                    <Avatar
                      src={
                        notary.avatar?.thumbnailPipeFrom ||
                        getDefaultAvatarUrl(`${notary?.firstName} ${notary?.lastName}`)
                      }
                    />
                  </Col>
                  <Col className='gx-flex-row gx-pl-1'>
                    <TextOnlyStyled className='notary-name line-clamp-2' $color='#545454'>
                      {notaryFullName}
                    </TextOnlyStyled>
                  </Col>
                </div>
                <TextOnlyButtonStyled className='gx-ml-1' onClick={assignNotaryClick}>
                  Change
                </TextOnlyButtonStyled>
              </>
            )}
            {!notary && (
              <>
                {isAppointmentRequested && (
                  <Col className='gx-pl-0 gx-mr-2'>
                    <TextOnlyStyled $color='#F7CB73'>Requested</TextOnlyStyled>
                  </Col>
                )}
                <Col className='gx-p-0'>
                  <TextOnlyButtonStyled onClick={assignNotaryClick}>
                    {isAppointmentRequested ? 'Detail' : '+ Notary'}
                  </TextOnlyButtonStyled>
                </Col>
              </>
            )}
          </div>
        );

      default:
        return (
          <Row className='gx-m-0'>
            {notary && (
              <Row align='middle' className='gx-m-0'>
                <Col flex='32'>
                  <Avatar
                    src={
                      notary.avatar?.thumbnailPipeFrom ||
                      getDefaultAvatarUrl(`${notary?.firstName} ${notary?.lastName}`)
                    }
                  />
                </Col>
                <Col>
                  <NotaryNameStyled className='gx-m-0 gx-pl-1 notary-name' $color='#545454'>
                    {notary?.firstName} {notary?.lastName}
                  </NotaryNameStyled>
                </Col>
              </Row>
            )}
            {!notary && (
              <Col className='gx-pl-0 gx-mr-2'>
                {isAppointmentRequested ? (
                  <TextOnlyStyled $color='#038fde'>Waiting for Notary approval</TextOnlyStyled>
                ) : (
                  <TextOnlyStyled $color='#F7CB73'>Awaiting Notary Assignment</TextOnlyStyled>
                )}
              </Col>
            )}
          </Row>
        );
    }
  }, [selectedAppointment]);

  const paymentField: DataFieldProps = useMemo(() => {
    const { clientPrice, notaryPrice } = getPaymentText(selectedAppointment, transactionTypesData);
    switch (userRole) {
      case RoleEnum.ADMIN:
        return {
          value: notaryPrice !== undefined ? `$${notaryPrice}` : 'Not decided',
          label: 'Notary Cost',
          sideFields: [{ label: 'Client Price', value: `$${clientPrice}`, size: 10 }],
        };
      case RoleEnum.CLIENT:
        return { label: 'Cost', value: `$${clientPrice}` };
      case RoleEnum.NOTARY:
        return { label: 'Payment', value: `$${notaryPrice}` };
      default:
        return { label: 'Payment', value: `$${notaryPrice}` };
    }
  }, [selectedAppointment, userRole, transactionTypesData]);

  const selectedAppointmentField: DataFieldProps[] = useMemo(() => {
    const appointmentFiles = selectedAppointment?.files;
    const linkedDocumentFields =
      isAdmin || (isNotary && !notary)
        ? []
        : [
            {
              label: 'Linked Documents',
              value: (
                <div>
                  {(appointmentFiles?.length || 0) > 0
                    ? appointmentFiles?.map((file) => (
                        <LinkedDocument
                          id={file.id}
                          name={file.name}
                          description={file.details.description}
                          key={file.id}
                          size={file.size}
                        />
                      ))
                    : 'No linked documents.'}
                </div>
              ),
            },
          ];

    return [
      {
        label: 'Signatories',
        value: selectedSignatories,
        sideFields: [
          {
            label: 'Notary',
            value: NotaryDataField,
            size: 10,
          },
        ],
      },
      {
        label: 'Description',
        value: description,
      },
      { ...paymentField },
      {
        label: 'Duration',
        value: `${duration / 3600}h`,
        sideFields: [
          {
            label: 'Date & Time',
            value: dateTime,
            size: 10,
          },
        ],
      },
      {
        label: 'Address',
        value: <AddressFormatComponent addressDetail={address} separateLines />,
      },
      ...linkedDocumentFields,
    ];
  }, [selectedAppointment, userRole]);

  return (
    <>
      {Modal}
      <Row className='gx-w-100 gx-m-0' style={{ padding: '20px 0' }}>
        <Col flex={1} className='gx-p-0'>
          <InputFieldsGenerator dataFields={selectedAppointmentField} />
        </Col>
        <Col span={24} style={{ maxWidth: '100%', maxHeight: '500px' }} className='gx-p-0'>
          <MapDirection hideDirectionBox location={address} style={{ width: '100%', height: 300, marginTop: 10 }} />
        </Col>
      </Row>
    </>
  );
};

export default React.memo(AppointmentSlideIn);
